import React, { useEffect, useState } from "react";
import type { StudentReviewContext, StudentReviewDataLoadedStatus } from "p6m-contexts";
import type { StudentTestAnswer, StudentTestContent, TestResultShareData } from "p6m-p6u";
import { StudentReviewResultLoadStatuses } from "../enums/studentReview";
import { getRequest } from "../helpers/networkHelper";

const StudentReviewTestContext = React.createContext<StudentReviewContext | undefined>(undefined);

function useStudentReviewContext() {
    const context = React.useContext(StudentReviewTestContext);

    if (!context) {
        throw new Error("useTestReviewContext must be used inside the provider");
    }

    return {
        ...context,
    };
}

function StudentReviewTestContextProvider(props: any) {
    const [shareId, setShareId] = useState("");
    const [testId, setTestId] = useState("");
    const [resultId, setResultId] = useState("");
    const [dataLoaded, setDataLoaded] = useState<StudentReviewDataLoadedStatus>("START");
    const [resultLoadStatus, setResultLoadStatus] = useState<StudentReviewResultLoadStatuses>(
        StudentReviewResultLoadStatuses.PENDING
    );
    const [resultData, setResultData] = useState<StudentTestAnswer>({});
    const [studentTest, setStudentTest] = useState<StudentTestContent>({});

    useEffect(() => {
        async function loadResultData(resultId: string, testId: string) {
            try {
                const gradesData = await getRequest(`aea/student-tests/results/?testId=${testId}&resultId=${resultId}`);
                if (!gradesData.data) {
                    setResultLoadStatus(StudentReviewResultLoadStatuses.ERROR);
                    return;
                }
                setResultData({ ...gradesData.data });

                // Not optional: without the test there are no points, so the result would read as
                // "you scored nothing" rather than as a failure. Same endpoint the test-taking flow
                // uses, so a failure here is a network one, not a permission one.
                const studentTestResponse = await getRequest(`aea/student-tests/?testId=${testId}`);
                if (!studentTestResponse.data) {
                    setResultLoadStatus(StudentReviewResultLoadStatuses.ERROR);
                    return;
                }
                setStudentTest({ ...studentTestResponse.data, id: testId });

                setResultLoadStatus(StudentReviewResultLoadStatuses.LOADED);
            } catch (e) {
                console.log("Error loading data ", e);
                setResultLoadStatus(StudentReviewResultLoadStatuses.ERROR);
            }
        }
        if (resultId && testId) {
            // A share-link change re-runs this effect: block rendering on the stale
            // result again until the new one arrives.
            setResultLoadStatus(StudentReviewResultLoadStatuses.PENDING);
            setResultData({});
            setStudentTest({});
            loadResultData(resultId, testId);
        }
    }, [resultId, testId]);

    useEffect(() => {
        async function loadFeedbackData(shareId: string) {
            try {
                const feedbackData = await getRequest(`aea/feedbacks/?feedbackId=${shareId}`);
                if (feedbackData.data) {
                    const sharedIdData: TestResultShareData = { ...feedbackData.data };
                    setTestId(sharedIdData.testId || "");
                    setResultId(sharedIdData.resultId || "");
                    setDataLoaded("LOADED");
                    if (!sharedIdData.testId || !sharedIdData.resultId) {
                        // No result to fetch — the result effect never runs, so settle the status
                        // here or the access guard would wait forever on a fetch that never starts.
                        setResultLoadStatus(StudentReviewResultLoadStatuses.ERROR);
                    }
                } else {
                    setDataLoaded("NOT_FOUND");
                    setResultLoadStatus(StudentReviewResultLoadStatuses.ERROR);
                }
            } catch (e) {
                // Deliberately not NOT_FOUND: the request failed, so we never learned whether the
                // link is dead — leaving dataLoaded alone lets the page offer a retry.
                setResultLoadStatus(StudentReviewResultLoadStatuses.ERROR);
                console.log("Error loading feedbackdata", e);
            }
        }
        if (shareId) {
            loadFeedbackData(shareId);
        }
    }, [shareId]);

    const value = {
        shareId,
        setShareId,
        testId,
        setTestId,
        resultId,
        setResultId,
        dataLoaded,
        setDataLoaded,
        resultLoadStatus,
        resultData,
        setResultData,
        studentTest,
        setStudentTest,
    };

    return (
        <StudentReviewTestContext.Provider
            value={value}
            {...props}
        />
    );
}

export { StudentReviewTestContextProvider, useStudentReviewContext };
